import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import type { PublicWebFormConfigResponse } from '@orion/contracts';
import { ApiError, api } from '../../lib/api-client.js';
import { DynamicLeadForm } from './DynamicLeadForm.js';

function usePublicWebFormQuery(publicKey: string) {
  return useQuery({
    queryKey: ['public-web-form', publicKey],
    queryFn: () => api.get<PublicWebFormConfigResponse>(`/public/forms/${publicKey}`),
    retry: false,
  });
}

/** The iframe-friendly twin of `PublicWebFormPage` — no branded split
 * panel, no forced full-height layout, just the form card sized to its own
 * content so a real website can drop `<iframe src=".../embed/forms/KEY">`
 * straight into an existing page (Admin ▸ Web forms shows the exact
 * snippet, including a starting height, next to each form). */
export function EmbedWebFormPage() {
  const { publicKey } = useParams({ from: '/embed/forms/$publicKey' });
  const { data, isLoading, isError, error } = usePublicWebFormQuery(publicKey);

  if (isLoading) return <div className="p-6" />;

  if (isError || !data) {
    return (
      <div className="p-6 text-center text-sm text-text-muted">
        {error instanceof ApiError && error.status === 404 ? "This form doesn't exist or has been turned off." : 'Could not load this form.'}
      </div>
    );
  }

  return (
    <div className="bg-surface p-6 font-sans text-ink">
      <h2 className="text-lg font-semibold text-ink">{data.name}</h2>
      <div className="mt-4">
        <DynamicLeadForm publicKey={publicKey} config={data} />
      </div>
      {data.branding.showPoweredBy && (
        <p className="mt-4 text-center text-[11px] text-text-muted">Powered by {data.branding.productName}</p>
      )}
    </div>
  );
}
